import React, { FC, forwardRef, useCallback } from 'react';
import styled from 'styled-components';
import { Button, ButtonProps } from '@material-ui/core';

import theme from '../../../../config/theme';
import { Icons, SvgIcon } from '../SvgIcon';
import { Tooltip } from '../Tooltip';
import { FontAwesomeSpriteIcon } from '../FontAwesomeLocal';
import { SpriteIcon } from 'src/config/api/models/FontAwesome';

const StyledButton = styled(
  ({
    size,
    dashed,
    bgColor,
    border,
    ...restProps
  }: Omit<ButtonProps, 'size'> & {
    size: number;
    dashed?: boolean;
    bgColor?: string;
    border?: boolean;
  }) => <Button {...restProps} />
)`
  position: relative;
  width: ${({ size }) => `${size}rem`};
  height: ${({ size }) => `${size}rem`};
  background-color: ${({ bgColor }) => (bgColor ? bgColor : undefined)};
  min-width: 0;
  min-height: 0;
  border: ${({ theme, dashed }) => (dashed ? theme.config.defaultBorder() : 'none')};
  border-color: ${({ border, color }) =>
    border ? theme.config.defaultBorder(color) : theme.config.defaultBorder()} !important;
  border-style: ${({ dashed }) => (dashed ? 'dashed' : 'none')};
  border-radius: 100%;
  flex-shrink: 0;
  z-index: 200;
  &:hover {
    background-color: ${({ bgColor }) => bgColor};
  }
`;

const StyledIcon = styled(({ iconCoordinates, ...restProps }) => <SvgIcon {...restProps} />)`
  position: absolute;
  top: ${({ iconCoordinates }) => iconCoordinates?.top || '50%'};
  left: ${({ iconCoordinates }) => iconCoordinates?.left || '50%'};
  transform: ${({ iconCoordinates }) => iconCoordinates?.translate || 'translate(-46%, -46%)'};
  width: 100%;
`;

const BtnWithRef = forwardRef<
  any,
  Omit<ButtonProps, 'size'> & {
    size: number;
    dashed?: boolean;
    bgColor: undefined | string;
    border?: boolean;
  }
>((props, ref) => <StyledButton {...props} ref={ref || undefined} />);

type Props = Omit<ButtonProps, 'size' | 'color'> & {
  icon?: Icons;
  faIcon?: SpriteIcon;
  color?: string;
  bgColor?: string;
  small?: boolean;
  large?: boolean;
  tooltip?: string;
  dashed?: boolean;
  disabled?: boolean;
  largeIcon?: boolean;
  size?: number;
  border?: boolean;
  iconCoordinates?: unknown;
};

const IconButton: FC<Props> = ({
  icon,
  faIcon,
  color,
  bgColor = undefined,
  small = false,
  large = false,
  tooltip = undefined,
  dashed,
  disabled,
  largeIcon = false,
  border = false,
  size = 4,
  iconCoordinates,
  ...restProps
}) => {
  const btnSize = small || faIcon ? 3 : large ? 5 : size;

  const iconRender = useCallback(() => {
    if (icon) {
      return (
        <StyledIcon
          small={small}
          large={large ? large : largeIcon}
          color={color || theme.palette.grey['600']}
          icon={icon}
          iconCoordinates={iconCoordinates}
        />
      );
    }
    if (faIcon) {
      return (
        <FontAwesomeSpriteIcon
          iconStyle={{ cursor: 'pointer' }}
          icon={faIcon}
          color={color || theme.palette.grey['600']}
          iconSize={small ? 'sm' : 'lg'}
        />
      );
    }
  }, [icon, faIcon, color, large, largeIcon, small, iconCoordinates]);

  const btn =
    tooltip && !disabled ? (
      <BtnWithRef
        size={btnSize}
        dashed={dashed}
        bgColor={bgColor}
        disabled={disabled}
        {...restProps}
      >
        {iconRender()}
      </BtnWithRef>
    ) : (
      <StyledButton
        style={{ border: border ? `1px solid ${color}` : undefined }}
        size={btnSize}
        dashed={dashed}
        bgColor={bgColor}
        disabled={disabled}
        border={border}
        {...restProps}
      >
        {iconRender()}
      </StyledButton>
    );

  return tooltip && !disabled ? <Tooltip title={tooltip}>{btn}</Tooltip> : btn;
};

export default IconButton;
